import type { ShipTier } from "@voidex/contracts-ts";
import { useEffect, useState } from "react";
import { formatCountdown } from "../../services/zoneUtils";
import { ShipIcon } from "../shared/ShipIcon";

interface MissionAnimationProps {
	shipTier: ShipTier;
	planetName: string;
	planetColor: string;
	systemName: string;
	expectedDuration: number;
	onAffirmative: () => void;
	onViewMission: () => void;
}

const STATUS_PHASES = [
	{ text: "Launching from dock...", delay: 0 },
	{ text: "Entering hyperspace...", delay: 3000 },
	{ text: "Mining resources...", delay: 5000 },
];

export function MissionAnimation({
	shipTier,
	planetName,
	planetColor,
	systemName,
	expectedDuration,
	onAffirmative,
	onViewMission,
}: MissionAnimationProps) {
	const [statusIndex, setStatusIndex] = useState(0);
	const [remaining, setRemaining] = useState(expectedDuration);

	useEffect(() => {
		const timers = STATUS_PHASES.slice(1).map((phase, i) =>
			setTimeout(() => setStatusIndex(i + 1), phase.delay),
		);
		return () => timers.forEach(clearTimeout);
	}, []);

	useEffect(() => {
		if (remaining <= 0) return;
		const interval = setInterval(() => {
			setRemaining((prev) => Math.max(0, prev - 1));
		}, 1000);
		return () => clearInterval(interval);
	}, [remaining]);

	const statusText =
		statusIndex === 2
			? `Mining resources on ${planetName}...`
			: statusIndex === 1
				? `Approaching ${planetName}...`
				: "Launching from dock...";

	return (
		<div className="mission-anim">
			{/* Starfield layers */}
			<div className="mission-anim__starfield mission-anim__starfield--far" />
			<div className="mission-anim__starfield mission-anim__starfield--mid" />
			<div className="mission-anim__starfield mission-anim__starfield--near" />

			{/* Target planet */}
			<div className="mission-anim__planet-wrapper">
				<div
					className="mission-anim__planet"
					style={{
						background: `radial-gradient(circle at 35% 35%, ${planetColor}cc, ${planetColor}44)`,
						boxShadow: `0 0 40px ${planetColor}66, 0 0 80px ${planetColor}22`,
					}}
				/>
				<div className="mission-anim__planet-ring" style={{ borderColor: `${planetColor}33` }} />
			</div>

			{/* Spaceship */}
			<div
				className={`mission-anim__ship ${statusIndex >= 1 ? "mission-anim__ship--arrived" : "mission-anim__ship--entering"}`}
			>
				<ShipIcon tier={shipTier} size="lg" />
			</div>

			{/* Mining particles (visible after ship arrives) */}
			{statusIndex >= 2 && (
				<div className="mission-anim__particles">
					{[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
						<div
							key={`particle-m${n}`}
							className="mission-anim__particle"
							style={{
								background: planetColor,
								animationDelay: `${n * 0.4}s`,
								left: `${60 + n * 1.9}%`,
							}}
						/>
					))}
				</div>
			)}

			{/* Status text */}
			<div className="mission-anim__footer">
				<span className="mission-anim__status">{statusText}</span>
				<span className="mission-anim__system">{systemName} System</span>
			</div>

			{/* ETA overlay with Affirmative button */}
			<div className="mission-anim__overlay">
				<div className="mission-anim__eta">
					{remaining > 0 ? `Ship will arrive in ${formatCountdown(remaining)}` : "Mission complete"}
				</div>
				<button
					type="button"
					className="mission-anim__affirmative-btn"
					onClick={remaining > 0 ? onAffirmative : onViewMission}
				>
					{remaining > 0 ? "Affirmative" : "View Mission"}
				</button>
			</div>
		</div>
	);
}
